// src/components/AutoLogin.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, AlertCircle, RefreshCcw } from 'lucide-react';

const AutoLogin = ({ onLoginSuccess }) => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [retryCount, setRetryCount] = useState(0);
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000; // 2 seconds

    const logMessage = (message, data = {}) => {
        console.log(`[AutoLogin] ${message}`, data);
    };

    const performLogin = async (loginToken, attemptNumber = 0) => {
        logMessage('Starting auto-login process', { attemptNumber });
        logMessage('Token:', loginToken);

        try {
            // Don't send a body at all
            const response = await fetch(`/api/auth/auto-login/${loginToken}`, {
                method: 'POST',
                credentials: 'include'
            });

            // Handle non-JSON responses
            const contentType = response.headers.get('content-type');
            let data;

            try {
                if (contentType && contentType.includes('application/json')) {
                    data = await response.json();
                } else {
                    const text = await response.text();
                    throw new Error(`Unexpected response: ${text}`);
                }
            } catch (parseError) {
                console.error('Response parsing error:', parseError);
                throw new Error('Failed to parse server response');
            }

            if (!response.ok) {
                throw new Error(data.error || 'Auto-login failed');
            }

            // Store user data
            if (data.organization) {
                sessionStorage.setItem('currentOrganization', 
                    JSON.stringify(data.organization)
                );
            }

            sessionStorage.setItem('userData', JSON.stringify({
                id: data.id,
                email: data.email,
                name: data.name,
                is_admin: Boolean(data.is_admin)
            }));

            sessionStorage.setItem('autoLoginSuccess', 'true');

            // Notify parent
            onLoginSuccess({
                id: data.id,
                email: data.email,
                name: data.name,
                is_admin: Boolean(data.is_admin)
            });

            // Navigate
            if (data.is_admin) {
                navigate('/admin-dashboard');
            } else {
                navigate('/requirements');
            }

            logMessage('Auto-login successful');

        } catch (error) {
            logMessage('Error:', error);
            
            const isConnectionError = error.message.includes('Failed to fetch') || 
                                    error.message.includes('NetworkError') ||
                                    error.message.includes('Proxy error');

            if (isConnectionError && attemptNumber < MAX_RETRIES) {
                setError(`Connection error. Retrying... (${attemptNumber + 1}/${MAX_RETRIES})`);
                setRetryCount(attemptNumber + 1);
                await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
                return performLogin(loginToken, attemptNumber + 1);
            }

            setError(error.message || 'Auto-login failed');
            setTimeout(() => navigate('/'), 5000);
        } finally {
            if (attemptNumber === MAX_RETRIES - 1) {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!token) {
            setError('No login token provided');
            setIsLoading(false);
            return;
        }

        performLogin(token);
    }, [token]);

    const handleRetry = () => {
        setIsLoading(true);
        setError(null);
        setRetryCount(0);
        performLogin(token);
    };

    if (isLoading) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
                    <div className="flex flex-col items-center space-y-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                        <p className="text-lg font-medium text-gray-900">
                            {retryCount > 0 ? `Retrying... (${retryCount}/${MAX_RETRIES})` : 'Logging you in...'}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center space-x-2 text-red-600">
                            <AlertCircle className="w-6 h-6" />
                            <h2 className="text-xl font-semibold">Login Failed</h2>
                        </div>
                        <button
                            onClick={() => navigate('/')}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    <div className="space-y-4">
                        <p className="text-gray-600">{error}</p>
                        {retryCount < MAX_RETRIES && (
                            <button
                                onClick={handleRetry}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                            >
                                <RefreshCcw className="w-4 h-4" />
                                Retry Connection
                            </button>
                        )}
                        <p className="text-sm text-gray-500">
                            You will be redirected to the login page in a few seconds...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default AutoLogin;